import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Target,
  Calendar,
  Award,
  TrendingUp,
  Users,
  Clock,
  Star,
  Loader2,
  Brain,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { performanceApi, employeeApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import GoalsAndReviews from './GoalsAndReviews';
import KnowledgeTransfers from './KnowledgeTransfers';

const Performance: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('goalsReviews');

  // Fetch performance data
  const { data: reviews = [], isLoading: reviewsLoading } = useQuery({
    queryKey: ['performanceReviews'],
    queryFn: () => performanceApi.reviews.getAll(),
  });

  const { data: goals = [], isLoading: goalsLoading } = useQuery({
    queryKey: ['performanceGoals'],
    queryFn: () => performanceApi.goals.getAll(),
  });

  const { data: feedback = [] } = useQuery({
    queryKey: ['feedback'],
    queryFn: () => performanceApi.feedback.getAll(),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  const isLoading = reviewsLoading || goalsLoading;

  // Calculate metrics
  const reviewsArray = Array.isArray(reviews) ? reviews : [];
  const goalsArray = Array.isArray(goals) ? goals : [];
  const employeesArray = Array.isArray(employees) ? employees : [];
  
  const completedReviews = reviewsArray.filter((r: any) => r.status === 'COMPLETED').length;
  const pendingReviews = reviewsArray.filter((r: any) => r.status === 'PENDING' || r.status === 'IN_PROGRESS').length;
  const overdueReviews = reviewsArray.filter((r: any) => r.status === 'OVERDUE').length;

  const activeGoals = goalsArray.filter((g: any) => g.status === 'IN_PROGRESS' || g.status === 'ON_TRACK').length;
  const completedGoals = goalsArray.filter((g: any) => g.status === 'COMPLETED').length;
  const atRiskGoals = goalsArray.filter((g: any) => g.status === 'AT_RISK' || g.status === 'BEHIND').length;

  const getEmployeeName = (employeeId: string) => {
    const employee = employeesArray.find((e: any) => e.id === employeeId);
    return employee
      ? `${employee.first_name} ${employee.last_name || ''}`
      : 'Unknown';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return 'default';
      case 'IN_PROGRESS':
      case 'ON_TRACK':
        return 'secondary';
      case 'PENDING':
        return 'outline';
      case 'OVERDUE':
      case 'AT_RISK':
      case 'BEHIND':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getGoalProgress = (goal: any) => {
    if (goal.progress !== undefined) return goal.progress;
    if (goal.status === 'COMPLETED') return 100;
    if (goal.status === 'IN_PROGRESS') return 50;
    return 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading performance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Performance Management</h1>
          <p className="text-gray-600 mt-2">Track reviews, goals, and employee performance</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/performance/goals')}
          >
            <Target className="h-4 w-4 mr-2" />
            Manage Goals
          </Button>
          <Button onClick={() => navigate('/performance/reviews')}>
            <Award className="h-4 w-4 mr-2" />
            New Review
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reviewsArray.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {completedReviews} completed, {pendingReviews} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Goals</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeGoals}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {completedGoals} completed, {atRiskGoals} at risk
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Feedback Requests</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{feedback.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Pending responses</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Performance</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4.2/5</div>
            <p className="text-xs text-muted-foreground mt-1">Based on reviews</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="goalsReviews">
            <Award className="h-4 w-4 mr-2" />
            Goals & Reviews
          </TabsTrigger>
          <TabsTrigger value="knowledgeTransfer">
            <Brain className="h-4 w-4 mr-2" />
            Knowledge Transfer
          </TabsTrigger>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
        </TabsList>

        {/* Goals & Reviews Tab */}
        <TabsContent value="goalsReviews">
          <GoalsAndReviews />
        </TabsContent>

        {/* Knowledge Transfer Tab */}
        <TabsContent value="knowledgeTransfer">
          <KnowledgeTransfers />
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Performance Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Review Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Completed</span>
                    <span className="font-medium">{completedReviews}</span>
                  </div>
                  <Progress value={(completedReviews / (reviewsArray.length || 1)) * 100} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Pending</span>
                    <span className="font-medium">{pendingReviews}</span>
                  </div>
                  <Progress value={(pendingReviews / (reviewsArray.length || 1)) * 100} className="h-2 bg-yellow-100" />
                </div>
                {overdueReviews > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Overdue</span>
                      <span className="font-medium text-red-600">{overdueReviews}</span>
                    </div>
                    <Progress value={(overdueReviews / (reviewsArray.length || 1)) * 100} className="h-2 bg-red-100" />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Goals Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Completed</span>
                    <span className="font-medium">{completedGoals}</span>
                  </div>
                  <Progress value={(completedGoals / (goalsArray.length || 1)) * 100} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Active</span>
                    <span className="font-medium">{activeGoals}</span>
                  </div>
                  <Progress value={(activeGoals / (goalsArray.length || 1)) * 100} className="h-2 bg-blue-100" />
                </div>
                {atRiskGoals > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>At Risk</span>
                      <span className="font-medium text-orange-600">{atRiskGoals}</span>
                    </div>
                    <Progress value={(atRiskGoals / (goalsArray.length || 1)) * 100} className="h-2 bg-orange-100" />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {reviewsArray.slice(0, 5).map((review: any) => (
                  <div key={review.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center space-x-3">
                      <Award className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">
                          Review for {getEmployeeName(review.employee_id)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(review.review_period_start)}
                        </p>
                      </div>
                    </div>
                    <Badge variant={getStatusColor(review.status)}>
                      {review.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>


      </Tabs>
    </div>
  );
};

export default Performance;